import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { CaseDeletionImpactSchema, type CaseDeletionImpact } from "@hector/shared-types";
import { apiClient } from "@/lib/apiClient";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import type { Case, UpdateCaseRequest, DeleteCaseResponse } from "@/types/api";
import type { CasesPage } from "@/hooks/useCases";
import { ErrorService } from '@/lib/errors/ErrorService';

/**
 * Cherche un dossier déjà présent dans le cache de la liste (`['cases', …]`).
 *
 * WHY : à l'ouverture d'un dossier depuis la liste, `GET /cases/:id` prend
 * quelques centaines de ms. Sans seed, `CaseView` affiche une carte blanche
 * « Chargement… » alors que le résumé du dossier (titre, métadonnées) est déjà
 * en cache via `useCases`. On l'utilise comme `placeholderData` pour peindre la
 * page instantanément, puis le détail complet le remplace dès l'arrivée.
 *
 * Matché par `id` exact → aucune fuite de données entre dossiers/cabinets
 * (contrairement à `keepPreviousData` qui montrerait l'ancien dossier).
 */
function findCaseInListCache(queryClient: QueryClient, id: string): Case | undefined {
  const listQueries = queryClient.getQueriesData<{ pages?: CasesPage[] }>({ queryKey: ['cases'] });
  for (const [, data] of listQueries) {
    const pages = data?.pages;
    if (!pages) continue;
    for (const page of pages) {
      const found = page.items?.find((c) => c.id === id);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Hook pour récupérer un dossier spécifique
 * Migrated to Backend API
 *
 * @param options.staleTime - Surcharge PAR OBSERVATEUR du délai de fraîcheur.
 *   Un composant qui ne fait que LIRE le dossier déjà chargé par `CaseView`
 *   passe `Infinity` : sans ça, chaque remontage (les onglets Radix démontent
 *   leur contenu) relancerait `GET /cases/:id` une fois les 30 s écoulées.
 *   L'invalidation de `['case', id]` le rafraîchit quand même.
 */
export const useCase = (id: string | undefined, options?: { staleTime?: number }) => {
  const queryClient = useQueryClient();

  return useQuery<Case | null>({
    queryKey: ['case', id],
    queryFn: async () => {
      if (!id) return null;

      try {
        const caseData = await apiClient.get<Case>(`/cases/${id}`);
        return caseData;
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_case' }, silent: true });
        throw error;
      }
    },
    enabled: !!id,
    staleTime: options?.staleTime ?? 30000, // Cache for 30 seconds
    // Seed instantané depuis la liste → supprime la carte blanche à l'ouverture
    // d'un dossier déjà listé (cf. findCaseInListCache + issue #238).
    placeholderData: id ? () => findCaseInListCache(queryClient, id) : undefined,
  });
};

/**
 * Hook pour récupérer un dossier avec le résumé
 * Migrated to Backend API
 */
export const useCaseWithSummary = (id: string | undefined) => {
  return useQuery<Case | null>({
    queryKey: ['case', id, 'summary'],
    queryFn: async () => {
      if (!id) return null;

      try {
        const caseData = await apiClient.get<Case>(`/cases/${id}/summary`);
        return caseData;
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_case_summary' }, silent: true });
        throw error;
      }
    },
    enabled: !!id,
  });
};

/**
 * Hook pour récupérer un dossier avec le compteur de documents
 * Migrated to Backend API
 */
export const useCaseWithDocumentsCount = (id: string | undefined) => {
  return useQuery<Case & { documents_count?: number } | null>({
    queryKey: ['case', id, 'with-documents-count'],
    queryFn: async () => {
      if (!id) return null;

      try {
        const caseData = await apiClient.get<Case & { documents_count?: number }>(`/cases/${id}/with-documents-count`);
        return caseData;
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_case_documents_count' }, silent: true });
        throw error;
      }
    },
    enabled: !!id,
  });
};

/**
 * Hook pour mettre à jour un dossier
 * Migrated to Backend API
 */
export const useUpdateCase = (caseId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updatedData: UpdateCaseRequest) => {
      return await apiClient.patch<Case>(`/cases/${caseId}`, updatedData);
    },
    onSuccess: (data, updatedData) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      queryClient.setQueryData(['case', caseId], data);
      if (updatedData.status !== undefined) {
        // Archiving/restoring a dossier cascades personal conversation state on
        // the server. Refresh both its scoped list and every global projection.
        queryClient.invalidateQueries({ queryKey: ['conversations', caseId] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    },
  });
};

/**
 * Ce que la suppression du dossier fera aux conversations, lu pendant que la
 * boîte de confirmation est ouverte (`GET /cases/:id/deletion-impact`, réservé
 * au propriétaire comme la suppression). Sous `['case', caseId]` pour que les
 * invalidations du dossier l'atteignent, avec le cabinet (règle des requêtes).
 *
 * `staleTime: 0` : le défaut de l'app (5 min) garderait le décompte d'une
 * ouverture précédente, alors qu'un confrère a pu créer une conversation entre
 * deux ; un avertissement de suppression se relit à chaque ouverture.
 * `retry: false` : les trois nouvelles tentatives par défaut laisseraient la
 * boîte sur « Décompte des conversations… » environ 7 s pendant que l'avocat
 * décide ; un échec affiche tout de suite la règle sans nombre.
 */
export const useCaseDeletionImpact = (caseId: string) => {
  const cabinetId = useCabinetId();
  return useQuery<CaseDeletionImpact>({
    queryKey: ['case', caseId, 'deletion-impact', cabinetId],
    queryFn: async () => {
      const impact = await apiClient.get<unknown>(`/cases/${caseId}/deletion-impact`);
      return CaseDeletionImpactSchema.parse(impact);
    },
    enabled: !!cabinetId,
    staleTime: 0,
    retry: false,
  });
};

/**
 * Hook pour supprimer un dossier
 * Migrated to Backend API
 */
export const useDeleteCase = () => {
  const queryClient = useQueryClient();
  
  return useMutation<DeleteCaseResponse, Error, string>({
    mutationFn: async (caseId: string) => {
      console.log('🗑️ [useDeleteCase] mutationFn appelée avec caseId:', caseId);
      console.log('🗑️ [useDeleteCase] Appel API DELETE /cases/' + caseId);
      
      try {
        const result = await apiClient.delete<DeleteCaseResponse>(`/cases/${caseId}`);
        console.log('🗑️ [useDeleteCase] ✅ API DELETE réussie, résultat:', result);
        return result;
      } catch (error) {
        console.error('🗑️ [useDeleteCase] ❌ Erreur API DELETE:', error);
        throw error;
      }
    },
    onSuccess: (data, caseId) => {
      console.log('🗑️ [useDeleteCase] onSuccess - Invalidation des queries');
      console.log('🗑️ [useDeleteCase] Données:', data, 'CaseId:', caseId);
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', caseId] });
    },
    onError: (error) => {
      ErrorService.handle(error, { context: { action: 'delete_case' } });
    },
  });
};
